import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProjectModal = ({ project, onClose }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [showShare, setShowShare] = useState(false); 
  const [copied, setCopied] = useState(false); 

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Eye' },
    { id: 'objectives', label: 'Objectives', icon: 'Target' },
    { id: 'challenges', label: 'Challenges', icon: 'AlertTriangle' },
    { id: 'solutions', label: 'Solutions', icon: 'Lightbulb' },
    { id: 'results', label: 'Results', icon: 'TrendingUp' }
  ];

  const nextImage = () => {
    setActiveImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setActiveImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = project.downloadUrl;
    link.download = `${project.title.replace(/\s+/g, '-').toLowerCase()}-case-study.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //  Title + URL share text
  const shareText = `${project.title} - ${window.location.href}`;

  // Updated handleCopy with fallback
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch (err) {
      const textarea = document.createElement("textarea");
      textarea.value = window.location.href;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
    }
    setTimeout(() => setCopied(false), 1000); 
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-surface rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-bold text-text-primary">
              {project.title}
            </h2>
            <p className="text-text-secondary">{project.client}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary transition-colors duration-200 rounded-md hover:bg-secondary-100"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Image Gallery */}
          <div className="w-1/2 relative bg-black">
            <Image
              src={project.images[activeImageIndex]}
              alt={`${project.title} - Image ${activeImageIndex + 1}`}
              className="w-full h-full object-cover"
            />

            {/* Image Navigation */}
            {project.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all duration-200"
                >
                  <Icon name="ChevronLeft" size={20} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all duration-200"
                >
                  <Icon name="ChevronRight" size={20} />
                </button>

                {/* Image Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {project.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-200 ${index === activeImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                        }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Content */}
          <div className="w-1/2 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-border overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors duration-200 ${activeTab === tab.id
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-text-secondary hover:text-text-primary'
                    }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-3">Project Overview</h3>
                    <p className="text-text-secondary leading-relaxed mb-6">
                      {project.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-text-primary mb-2">Industry</h4>
                      <p className="text-text-secondary">{project.industry}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary mb-2">Project Size</h4>
                      <p className="text-text-secondary">{project.projectSize}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary mb-2">Completion Date</h4>
                      <p className="text-text-secondary">
                        {new Date(project.completionDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary mb-2">Category</h4>
                      <p className="text-text-secondary">{project.category}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-text-primary mb-3">Technology Stack</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="bg-primary-50 text-primary px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {project.testimonial && (
                    <div className="bg-secondary-50 p-4 rounded-lg">
                      <h4 className="font-medium text-text-primary mb-2">Client Testimonial</h4>
                      <blockquote className="text-text-secondary italic mb-2">
                        "{project.testimonial.text}"
                      </blockquote>
                      <cite className="text-sm text-text-secondary">
                        — {project.testimonial.author}, {project.testimonial.position}
                      </cite>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'objectives' && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-4">Project Objectives</h3>
                  <div className="prose prose-sm max-w-none text-text-secondary">
                    <p>{project.objectives}</p>
                  </div>
                </div>
              )}

              {activeTab === 'challenges' && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-4">Technical Challenges</h3>
                  <div className="prose prose-sm max-w-none text-text-secondary">
                    <p>{project.challenges}</p>
                  </div>
                </div>
              )}

              {activeTab === 'solutions' && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-4">Solutions Implemented</h3>
                  <div className="prose prose-sm max-w-none text-text-secondary">
                    <p>{project.solutions}</p>
                  </div>
                </div>
              )}

              {activeTab === 'results' && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-4">Project Results</h3>
                  <div className="prose prose-sm max-w-none text-text-secondary">
                    <p>{project.results}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            {/* <div className="border-t border-border p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* <button
                    onClick={handleDownload}
                    className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors duration-200"
                  >
                    <Icon name="Download" size={16} />
                    <span>Download Case Study</span>
                  </button> 
                  <button className="flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-colors duration-200">
                    <Icon name="Share2" size={16} />
                    <span>Share</span>
                  </button>
                </div>
                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon name="Eye" size={16} />
                  <span>Case Study</span>
                </div>
              </div>
            </div> */}

            {/*second footer*/}

            <div className="border-t border-border p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 relative">

                  <button
                    onClick={() => setShowShare(true)}
                    className="flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
                  >
                    <Icon name="Share2" size={16} />
                    <span>Share</span>
                  </button>

                  {showShare && (
                    <div
                      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
                      onClick={() => setShowShare(false)}
                    >
                      <div
                        className="bg-white rounded-lg shadow-xl p-6 w-80"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h3 className="text-lg font-semibold mb-4 text-center">Share This Project</h3>

                        <div className="flex items-center border border-gray-300 rounded-md overflow-hidden mb-2">
                          <input
                            type="text"
                            value={window.location.href}
                            readOnly
                            className="flex-1 px-3 py-2 text-sm text-gray-700 outline-none"
                          />
                          <button
                            onClick={handleCopy}
                            className="px-3 py-2 bg-primary text-white text-sm font-medium hover:bg-primary-dark"
                          >
                            Copy
                          </button>
                        </div>

                        {copied && (
                          <p className="text-green-600 text-sm mb-3 text-center">
                             Link Copied!
                          </p>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <a
                            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                          >
                            <Icon name="MessageCircle" size={24} className="text-green-500" />
                            <span className="text-sm mt-1">WhatsApp</span>
                          </a>
                          <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(project.title)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                          >
                            <Icon name="Facebook" size={24} className="text-blue-600" />
                            <span className="text-sm mt-1">Facebook</span>
                          </a>
                          <a
                            href="https://www.instagram.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                          >
                            <Icon name="Instagram" size={24} className="text-pink-500" />
                            <span className="text-sm mt-1">Instagram</span>
                          </a>
                          <a
                            href={`mailto:?subject=${encodeURIComponent(project.title)}&body=${encodeURIComponent(shareText)}`}
                            className="flex flex-col items-center p-3 rounded-md hover:bg-gray-100"
                          >
                            <Icon name="Mail" size={24} className="text-red-500" />
                            <span className="text-sm mt-1">Email</span>
                          </a>
                        </div>

                        <button
                          onClick={() => setShowShare(false)}
                          className="mt-4 w-full py-2 rounded-md bg-primary text-white font-medium hover:bg-primary-dark"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon name="Eye" size={16} />
                  <span>Case Study</span>
                </div>
              </div>
            </div> 
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProjectModal;
